import { apiPost, apiGet, apiPut, apiDelete } from './apiUtils';

const billingService = {
  /**
   * Get current user's active subscription
   * @returns {Promise<Object>} - Response with subscription details
   */
  getCurrentSubscription: async () => {
    try {
      console.log('🚀 BillingService: Fetching current subscription...');
      console.log('📡 API Endpoint: /api/billing/subscriptions/current');
      console.log('🔑 Auth token:', localStorage.getItem('authToken') ? 'Present' : 'Missing');

      const response = await apiGet('/api/billing/subscriptions/current');
      console.log('📥 BillingService: Raw API response:', response);

      return {
        success: true,
        data: response,
        message: 'Subscription retrieved successfully'
      };
    } catch (error) {
      console.error('❌ BillingService: Error fetching current subscription:', error);
      console.error('❌ Error type:', typeof error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error stack:', error.stack);

      return {
        success: false,
        message: error.message || 'Failed to fetch subscription',
        error: error,
        data: null
      };
    }
  },

  /**
   * Check subscription status - matches mobile app exactly
   * @returns {Promise<Object>} - Response with subscription status
   */
  checkSubscriptionStatus: async () => {
    try {
      console.log('🚀 BillingService: Checking subscription status...');
      console.log('📡 API Endpoint: /api/billing/subscriptions/check');
      console.log('🔑 Auth token:', localStorage.getItem('authToken') ? 'Present' : 'Missing');

      const response = await apiGet('/api/billing/subscriptions/check');
      console.log('📥 BillingService: Raw subscription check response:', response);

      return {
        success: true,
        data: response,
        message: 'Subscription status retrieved successfully'
      };
    } catch (error) {
      console.error('❌ BillingService: Error checking subscription status:', error);

      return {
        success: false,
        message: error.message || 'Failed to check subscription status',
        error: error,
        data: null
      };
    }
  },

  /**
   * Get user invoices
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - Invoice list response
   */
  getUserInvoices: async (params = {}) => {
    try {
      console.log('🚀 BillingService: Fetching user invoices...', params);
      const queryString = new URLSearchParams(params).toString();
      const endpoint = `/api/billing/invoices${queryString ? `?${queryString}` : ''}`;
      console.log('📡 Full endpoint:', endpoint);
      console.log('🔑 Auth token:', localStorage.getItem('authToken') ? 'Present' : 'Missing');

      const response = await apiGet(endpoint);
      console.log('📥 BillingService: Raw invoices response:', response);

      return {
        success: true,
        data: response,
        message: 'Invoices retrieved successfully'
      };
    } catch (error) {
      console.error('❌ BillingService: Error fetching invoices:', error);
      console.error('❌ Error type:', typeof error);
      console.error('❌ Error message:', error.message);

      return {
        success: false,
        message: error.message || 'Failed to fetch invoices',
        error: error,
        data: []
      };
    }
  },

  /**
   * Get public subscription plans with filters (same as mobile app)
   * @param {Object} filters - { userType, period }
   * @returns {Promise<Object>} - Response with plans array
   */
  getPublicSubscriptionPlansWithFilters: async (filters = {}) => {
    try {
      console.log('🚀 BillingService: Fetching public subscription plans with filters...', filters);
      const queryParams = new URLSearchParams();

      if (filters.userType) {
        queryParams.append('userType', filters.userType);
      }
      if (filters.period) {
        queryParams.append('period', filters.period);
      }

      const queryString = queryParams.toString();
      const endpoint = `/api/billing/plans${queryString ? '?' + queryString : ''}`;
      console.log('📡 Public plans endpoint:', endpoint);

      const response = await apiGet(endpoint);
      console.log('📥 BillingService: Raw public plans response:', response);

      return {
        success: true,
        data: response,
        message: 'Public subscription plans retrieved successfully'
      };
    } catch (error) {
      console.error('❌ BillingService: Error fetching public subscription plans:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch public subscription plans',
        error: error,
        data: null
      };
    }
  },

  /**
   * Get subscription plans
   * @param {Object} filters - Optional filters
   */
  getSubscriptionPlans: async (filters = {}) => {
    try {
      const params = new URLSearchParams();

      if (filters.user_type) params.append('user_type', filters.user_type);
      if (filters.period) params.append('period', filters.period);
      if (filters.status) params.append('status', filters.status);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);

      const queryString = params.toString();
      const endpoint = `/api/admin/subscription-plans${queryString ? `?${queryString}` : ''}`;

      const response = await apiGet(endpoint);
      console.log('🔧 billingService raw response:', response);

      // Handle nested response structure
      let actualData = response;

      // If response has nested success/data structure, extract the inner data
      if (response.success && response.data && response.data.data) {
        actualData = response.data.data;
        console.log('🔧 Extracted nested data:', actualData);
      } else if (response.data && !response.plans) {
        // If response.data doesn't have plans directly, check if it's the nested structure
        actualData = response.data;
        console.log('🔧 Using direct data:', actualData);
      }

      return {
        success: true,
        data: actualData,
        message: 'Subscription plans retrieved successfully'
      };
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch subscription plans',
        error: error
      };
    }
  },

  /**
   * Create subscription plan
   * @param {Object} planData - Plan data
   */
  createSubscriptionPlan: async (planData) => {
    try {
      const response = await apiPost('/api/admin/subscription-plans', planData);
      return {
        success: true,
        data: response,
        message: 'Subscription plan created successfully'
      };
    } catch (error) {
      console.error('Error creating subscription plan:', error);
      return {
        success: false,
        message: error.message || 'Failed to create subscription plan',
        error: error
      };
    }
  },

  /**
   * Update subscription plan
   * @param {string} planId - Plan ID
   * @param {Object} planData - Updated plan data
   */
  updateSubscriptionPlan: async (planId, planData) => {
    try {
      const response = await apiPut(`/api/admin/subscription-plans/${planId}`, planData);
      return {
        success: true,
        data: response,
        message: 'Subscription plan updated successfully'
      };
    } catch (error) {
      console.error('Error updating subscription plan:', error);
      return {
        success: false,
        message: error.message || 'Failed to update subscription plan',
        error: error
      };
    }
  },

  /**
   * Delete subscription plan
   * @param {string} planId - Plan ID
   */
  deleteSubscriptionPlan: async (planId) => {
    try {
      const response = await apiDelete(`/api/admin/subscription-plans/${planId}`);
      return {
        success: true,
        data: response,
        message: 'Subscription plan deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting subscription plan:', error);
      return {
        success: false,
        message: error.message || 'Failed to delete subscription plan',
        error: error
      };
    }
  },

  /**
   * Get credit packages
   * @param {Object} filters - Optional filters
   */
  getCreditPackages: async (filters = {}) => {
    try {
      const params = new URLSearchParams();

      if (filters.status) params.append('status', filters.status);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);

      const queryString = params.toString();
      const endpoint = `/api/admin/credit-packages${queryString ? `?${queryString}` : ''}`;

      const response = await apiGet(endpoint);
      console.log('🔧 billingService credit packages raw response:', response);

      // Handle nested response structure
      let actualData = response;

      // If response has nested success/data structure, extract the inner data
      if (response.success && response.data && response.data.data) {
        actualData = response.data.data;
        console.log('🔧 Extracted nested credit packages data:', actualData);
      } else if (response.data && !response.packages) {
        // If response.data doesn't have packages directly, check if it's the nested structure
        actualData = response.data;
        console.log('🔧 Using direct credit packages data:', actualData);
      }

      return {
        success: true,
        data: actualData,
        message: 'Credit packages retrieved successfully'
      };
    } catch (error) {
      console.error('Error fetching credit packages:', error);
      return {
        success: false,
        message: error.message || 'Failed to fetch credit packages',
        error: error
      };
    }
  },

  /**
   * Create credit package
   * @param {Object} packageData - Package data
   */
  createCreditPackage: async (packageData) => {
    try {
      const response = await apiPost('/api/admin/credit-packages', packageData);
      return {
        success: true,
        data: response,
        message: 'Credit package created successfully'
      };
    } catch (error) {
      console.error('Error creating credit package:', error);
      return {
        success: false,
        message: error.message || 'Failed to create credit package',
        error: error
      };
    }
  },

  /**
   * Update credit package
   * @param {string} packageId - Package ID
   * @param {Object} packageData - Updated package data
   */
  updateCreditPackage: async (packageId, packageData) => {
    try {
      const response = await apiPut(`/api/admin/credit-packages/${packageId}`, packageData);
      return {
        success: true,
        data: response,
        message: 'Credit package updated successfully'
      };
    } catch (error) {
      console.error('Error updating credit package:', error);
      return {
        success: false,
        message: error.message || 'Failed to update credit package',
        error: error
      };
    }
  },

  /**
   * Delete credit package
   * @param {string} packageId - Package ID
   */
  deleteCreditPackage: async (packageId) => {
    try {
      const response = await apiDelete(`/api/admin/credit-packages/${packageId}`);
      return {
        success: true,
        data: response,
        message: 'Credit package deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting credit package:', error);
      return {
        success: false,
        message: error.message || 'Failed to delete credit package',
        error: error
      };
    }
  },

  /**
   * Create subscription with payment
   * @param {Object} data - Subscription and payment data
   * @returns {Promise<Object>} - Payment details
   */
  createSubscriptionWithPayment: async (data) => {
    try {
      console.log('🚀 BillingService: Creating subscription with payment...', data);
      const response = await apiPost('/api/billing/subscriptions/subscribe', data);
      console.log('📥 BillingService: Subscription payment response:', response);
      return {
        success: true,
        data: response,
        message: 'Subscription created successfully'
      };
    } catch (error) {
      console.error('❌ BillingService: Error creating subscription with payment:', error);
      return {
        success: false,
        message: error.message || 'Failed to create subscription with payment',
        error: error,
        data: null
      };
    }
  },

  /**
   * Upgrade subscription (for users with active subscriptions)
   * @param {Object} data - { new_plan_id, payment_method, quantity }
   * @returns {Promise<Object>} - Payment response with details
   */
  upgradeSubscription: async (data) => {
    try {
      console.log('🚀 BillingService: Upgrading subscription...', data);
      const response = await apiPost('/api/billing/subscriptions/upgrade', data);
      console.log('📥 BillingService: Upgrade response:', response);

      // Check if the API response indicates failure
      if (response && response.success === false) {
        console.log('❌ BillingService: API returned error response:', response);
        return {
          success: false,
          message: response.message || 'Upgrade failed',
          error: response.error || response,
          data: response.data || null
        };
      }

      return {
        success: true,
        data: response,
        message: 'Subscription upgraded successfully'
      };
    } catch (error) {
      console.error('❌ BillingService: Error upgrading subscription:', error);
      return {
        success: false,
        message: error.message || 'Failed to upgrade subscription',
        error: error,
        data: null
      };
    }
  },

  /**
   * Combined subscription purchase (premium + addons in single payment) - same as mobile app
   * @param {Object} data - { plan_id, addons: [{plan_id, quantity}], purchase_type: 'new'|'modify'|'renew'|'upgrade' }
   * @returns {Promise<Object>} - Payment details with calculated total
   */
  createCombinedSubscriptionWithPayment: async (data) => {
    try {
      console.log('🚀 BillingService: Creating combined subscription with payment...', data);
      const response = await apiPost('/api/subscriptions/purchase/initiate-combined', data);
      console.log('📥 BillingService: Combined subscription payment response:', response);
      return {
        success: true,
        data: response,
        message: 'Combined subscription created successfully'
      };
    } catch (error) {
      console.error('❌ BillingService: Error creating combined subscription with payment:', error);
      return {
        success: false,
        message: error.message || 'Failed to create combined subscription with payment',
        error: error,
        data: null
      };
    }
  },

  /**
   * Initiate credit purchase (same API as mobile app)
   * @param {string} packageId - Credit package ID
   * @returns {Promise<Object>} - Response with payment_url and transaction details
   */
  initiateCreditPurchase: async (data) => {
    try {
      // Handle both old format (packageId string) and new format (object with packageId and redirect_url)
      const packageId = typeof data === 'string' ? data : data.packageId;
      const payload = {
        package_id: packageId
      };

      // Add redirect URL if provided
      if (typeof data === 'object' && data.redirect_url) {
        payload.redirect_url = data.redirect_url;
      }

      console.log('🚀 BillingService: Initiating credit purchase for package:', packageId, 'with payload:', payload);

      const response = await apiPost('/api/credits/purchase/initiate', payload);

      console.log('📥 BillingService: Credit purchase response:', response);

      return {
        success: true,
        data: response,
        message: 'Credit purchase initiated successfully'
      };
    } catch (error) {
      console.error('❌ BillingService: Error initiating credit purchase:', error);
      return {
        success: false,
        message: error.message || 'Failed to initiate credit purchase',
        error: error,
        data: null
      };
    }
  },

  /**
   * Get admin credit price (matching mobile app exactly)
   * @returns {Promise<Object>} - Response with credit price value
   */
  getAdminCreditPrice: async () => {
    try {
      console.log('🚀 BillingService: Getting admin credit price...');
      const response = await apiGet('/api/admin/settings/1_credit_price');
      console.log('📥 BillingService: Admin credit price response:', response);
      return {
        success: true,
        value: response?.data?.value || 1,
        message: 'Admin credit price retrieved successfully'
      };
    } catch (error) {
      console.error('❌ BillingService: Error getting admin credit price:', error);
      return {
        success: false,
        message: error.message || 'Failed to get admin credit price',
        error: error,
        value: 1
      };
    }
  },

  /**
   * Update admin credit price (matching mobile app exactly)
   * @param {number} price - New credit price
   * @returns {Promise<Object>} - Response with success status
   */
  updateAdminCreditPrice: async (price) => {
    try {
      console.log('🚀 BillingService: Updating admin credit price to:', price);
      const response = await apiPost('/api/admin/settings/update', {
        key: '1_credit_price',
        value: price
      });
      console.log('📥 BillingService: Update admin credit price response:', response);
      return {
        success: true,
        data: response,
        message: 'Admin credit price updated successfully'
      };
    } catch (error) {
      console.error('❌ BillingService: Error updating admin credit price:', error);
      return {
        success: false,
        message: error.message || 'Failed to update admin credit price',
        error: error
      };
    }
  }
};

export default billingService;